import { createCachedSelector } from 're-reselect';
import { createSelector } from 'reselect';
import ParcelTemplate, { CarrierParcelTemplate, getName } from '~/types/ParcelTemplate';
import { ApplicationState } from '../store';

export const getTemplateTokenFromProps = (_: unknown, props: { templateToken: string }): string =>
  props.templateToken;

export const getParcelTemplateIdFromProps = (
  _: unknown,
  props: { parcelTemplateId: string },
): string => props.parcelTemplateId;

export const getParcelTemplateStore = (state: ApplicationState) => state.parcelTemplate;

export const getParcelTemplates = createSelector(
  getParcelTemplateStore,
  (parcelTemplateStore): readonly ParcelTemplate[] => {
    return [...parcelTemplateStore.parcelTemplates].sort((a, b) => {
      // sort first by descending creation date (i.e. newest first)
      const dateComparison = (b.objectCreated?.getTime() ?? 0) - (a.objectCreated?.getTime() ?? 0);
      if (dateComparison !== 0) return dateComparison;
      const aName = getName(a);
      const bName = getName(b);
      if (!aName || !bName) return 0;
      // then by ascending template name
      return aName.localeCompare(bName);
    });
  },
);

export const getCarrierParcelTemplates = createSelector(
  getParcelTemplateStore,
  (
    parcelTemplateStore,
  ): Readonly<{
    [key: string]: CarrierParcelTemplate[];
  }> => parcelTemplateStore.carrierParcelTemplates,
);

export const isLoadingParcels = createSelector(
  getParcelTemplateStore,
  (parcelTemplateStore) =>
    parcelTemplateStore.isLoadingCarrierParcelTemplates &&
    parcelTemplateStore.isLoadingParcelTemplates,
);

export const isDeleting = createSelector(
  getParcelTemplateStore,
  (parcelTemplatesStore): boolean => parcelTemplatesStore.isDeleting,
);

export const getCarrierParcelTemplateByToken = createCachedSelector(
  getCarrierParcelTemplates,
  getTemplateTokenFromProps,
  (carrierParcelTemplates, templateToken): Maybe<CarrierParcelTemplate> => {
    let carrierParcelTemplate: Maybe<CarrierParcelTemplate> = null;
    const carriers = Object.keys(carrierParcelTemplates);

    for (const carrier of carriers) {
      carrierParcelTemplate =
        carrierParcelTemplates[carrier].find(
          (carrierParcelTemplate) => carrierParcelTemplate.token === templateToken,
        ) || null;

      if (carrierParcelTemplate !== null) {
        break;
      }
    }

    return carrierParcelTemplate;
  },
)(getTemplateTokenFromProps);

export const getParcelTemplateById = createSelector(
  getParcelTemplates,
  getParcelTemplateIdFromProps,
  (parcelTemplates, parcelTemplateId): Maybe<ParcelTemplate> => {
    return (
      parcelTemplates.find((parcelTemplate) => parcelTemplate.objectId === parcelTemplateId) || null
    );
  },
);

export const hasDefaultParcelTemplate = createSelector(
  getParcelTemplates,
  (parcelTemplates): boolean => {
    return parcelTemplates.some((parcelTemplate) => parcelTemplate.isDefault);
  },
);

export const getDefaultParcelTemplate = createSelector(getParcelTemplates, (parcelTemplates) => {
  return parcelTemplates.find((parcelTemplate) => parcelTemplate.isDefault);
});
